import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import "../Pages/PrintForm/printForm.css";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  Spinner,
  FormFeedback,
  Alert,
  Progress,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import logo from "../../assets/images/opd-main-logo.png";

//redux
import { Link, useNavigate } from "react-router-dom";

import withRouter from "../../Components/Common/withRouter";
import SignContext from "../../contextAPI/Context/SignContext";
import useLoginAttempt from "../../hooks/useLoginAttempt";

const Login = () => {
  const url = `${process.env.REACT_APP_BASE_URL}`;

  const { loginUser } = useContext(SignContext);
  const navigate = useNavigate();
  const [UserInfo, setUserInfo] = useState({
    email: "",
    password: "",
  });

  const [locationConsent, setLocationConsent] = useState(false);
  const [ipConsent, setIpConsent] = useState(false);
  const [Error, setError] = useState("");
  const [Success, setSuccess] = useState("");
  const [confrimPasswordShow, setConfrimPasswordShow] = useState(false);
  const [buttnLoading, setButtnLoading] = useState(false);
  const [securityInfoModal, setSecurityInfoModal] = useState(false);

  // Use login attempt hook
  const {
    isLocked,
    attemptCount,
    attemptsRemaining,
    formattedTime,
    loading: statusLoading,
  } = useLoginAttempt(UserInfo.email);

  const handleChange = (e) => {
    setUserInfo({ ...UserInfo, [e.target.name]: e.target.value });
  };

  // Get user IP address
  const getUserIP = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {

      return "unknown";
    }
  };

  // Get user GPS location
  const getUserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {

          resolve({ latitude: null, longitude: null });
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        },
      );
    });
  };

  // Attempt count color
  const getAttemptColor = () => {
    if (attemptCount === 0) return "success";
    if (attemptCount === 1) return "warning";
    if (attemptCount === 2) return "danger";
    return "dark";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if account is locked
    if (isLocked) {
      setError(`Account locked. Please try again after ${formattedTime}`);
      return;
    }

    // Validate consents
    if (!locationConsent || !ipConsent) {
      setError("Please accept both IP and location tracking consent");
      setTimeout(() => setError(""), 3000);
      return;
    }

    setButtnLoading(true);
    setError("");
    setSuccess("");

    try {
      // Get IP and location
      const [userIP, userLocation] = await Promise.all([
        getUserIP(),
        getUserLocation(),
      ]);

      // API call with IP and location
      const res = await axios.post(`${url}/api/login`, {
        email: UserInfo.email,
        password: UserInfo.password,
        locationConsent: true,
        ipConsent: true,
        clientIP: userIP,
        clientLatitude: userLocation.latitude,
        clientLongitude: userLocation.longitude,
      });

      // Note: axios interceptor unwraps response.data, so res is the data object directly
      if (res.success) {
        // Store in both localStorage and sessionStorage for compatibility
        const authUser = {
          token: res.token,
          roles: res.roles,
          msg: res.msg,
        };

        window.localStorage.setItem("loggedIn", true);
        window.localStorage.setItem("authToken", res.token);
        window.localStorage.setItem("token", res.token); // For axios interceptor
        window.localStorage.setItem("user", JSON.stringify(res));
        window.localStorage.setItem("rights", JSON.stringify(res.roles));
        window.sessionStorage.setItem("authUser", JSON.stringify(authUser));

        setSuccess(res.msg);
        navigate("/dashboard");
      } else {
        setError(res.msg);
        setTimeout(() => {
          setError("");
        }, 3000);
      }

      setButtnLoading(false);
    } catch (error) {

      // Get the response data and status code
      const responseData = error.response?.data;
      const statusCode = error.response?.status;

      if (statusCode === 423) {
        // Account locked
        setError(responseData?.msg || "Account locked due to failed attempts");
      } else if (statusCode === 401) {
        // Invalid credentials
        const remaining = responseData?.attemptsRemaining || 0;
        setError(`Invalid credentials. ${remaining} attempts remaining.`);

        if (responseData?.warning) {
          setTimeout(() => {
            setError(responseData.warning);
          }, 2000);
        }
      } else if (statusCode === 429) {
        // Rate limit exceeded
        setError(
          responseData?.msg ||
            "Too many login attempts. Please try again later.",
        );
      } else {
        // Generic error
        setError(
          responseData?.msg ||
            error.message ||
            "An error occurred. Please try again.",
        );
      }

      setButtnLoading(false);

      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };

  document.title = "Login";
  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <img
                      src={logo}
                      alt="Viraj"
                      style={{ maxHeight: "90px" }}
                      className="mobile-resp"
                    ></img>
                  </div>
                  {/* <p className="mt-3 fs-15 fw-medium">
                    Premium Admin & Dashboard Template
                  </p> */}
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Welcome Back !</h5>
                      <p className="text-muted">Sign in to continue.</p>
                    </div>
                    {Error && Error ? (
                      <Alert
                        color="danger"
                        isOpen={!!Error}
                        toggle={() => setError("")}
                      >
                        {" "}
                        {Error}{" "}
                      </Alert>
                    ) : null}
                    {Success && Success ? (
                      <Alert
                        color="success"
                        isOpen={!!Success}
                        toggle={() => setSuccess("")}
                      >
                        {" "}
                        {Success}{" "}
                      </Alert>
                    ) : null}

                    {/* Account Status Alert */}
                    {isLocked && (
                      <Alert color="danger" isOpen={isLocked}>
                        <i className="ri-lock-line me-2"></i>
                        <strong>Account Locked</strong>
                        <br />
                        Your account is locked due to multiple failed login
                        attempts.
                        <br />
                        Time remaining: <strong>{formattedTime}</strong>
                      </Alert>
                    )}

                    {/* Attempt Warning */}
                    {!isLocked && attemptCount > 0 && (
                      <Alert
                        color={getAttemptColor()}
                        isOpen={attemptCount > 0}
                      >
                        <i className="ri-alert-line me-2"></i>
                        <strong>Login Attempt Warning</strong>
                        <br />
                        Failed attempts: {attemptCount}/3
                        <br />
                        Remaining attempts: {attemptsRemaining}
                        <br />
                        {attemptsRemaining === 1 && (
                          <small className="text-danger">
                            ⚠️ One more failed attempt will lock your account
                            for 24 hours.
                          </small>
                        )}
                        <Progress
                          value={(attemptCount / 3) * 100}
                          color={getAttemptColor()}
                          className="mt-2"
                        />
                      </Alert>
                    )}
                    <div className="p-2 mt-4">
                      <Form onSubmit={(e) => handleSubmit(e)}>
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            Email
                          </Label>
                          <Input
                            className="form-control"
                            placeholder="Enter email"
                            type="email"
                            name="email"
                            value={UserInfo.email}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            required
                            disabled={isLocked}
                          />
                        </div>

                        <div className="mb-3">
                          {/* <div className="float-end">
                            <Link to="/forgot-password" className="text-muted">
                              Forgot password?
                            </Link>
                          </div> */}
                          <Label
                            className="form-label"
                            htmlFor="password-input"
                          >
                            Password
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              className="form-control pe-5"
                              placeholder="Enter Password"
                              name="password"
                              type={confrimPasswordShow ? "text" : "password"}
                              value={UserInfo.password}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              required
                              disabled={isLocked}
                            />
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              id="password-addon"
                              style={{
                                backgroundColor: "rgb(236,228,230)",
                                border: "1px solid #fff",
                                color: "black",
                              }}
                              onClick={() =>
                                setConfrimPasswordShow(!confrimPasswordShow)
                              }
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                        </div>

                        {/* Security Permissions Section */}
                        <div className="alert alert-light border mb-3">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <h6 className="mb-0">
                              <i className="ri-shield-keyhole-line text-primary me-1"></i>
                              Security Permissions Required
                            </h6>
                            <Button
                              color="link"
                              size="sm"
                              className="p-0"
                              onClick={() => setSecurityInfoModal(true)}
                            >
                              <i
                                className="ri-information-line text-info"
                                style={{ fontSize: "20px" }}
                              ></i>
                              <span className="ms-1 text-info">Why?</span>
                            </Button>
                          </div>

                          {/* IP Consent Checkbox */}
                          <div className="form-check mb-2">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id="ipConsent"
                              checked={ipConsent}
                              onChange={(e) => setIpConsent(e.target.checked)}
                              disabled={isLocked}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="ipConsent"
                            >
                              <i className="ri-shield-check-line me-1 text-success"></i>
                              I agree to share my IP address for security
                              logging
                            </Label>
                          </div>

                          {/* Location Consent Checkbox */}
                          <div className="form-check mb-0">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id="locationConsent"
                              checked={locationConsent}
                              onChange={(e) =>
                                setLocationConsent(e.target.checked)
                              }
                              disabled={isLocked}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="locationConsent"
                            >
                              <i className="ri-map-pin-line me-1 text-success"></i>
                              I agree to share my location for security purposes
                            </Label>
                          </div>
                        </div>

                        {/* <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            value=""
                            id="auth-remember-check"
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="auth-remember-check"
                          >
                            Remember me
                          </Label>
                        </div> */}

                        {!buttnLoading ? (
                          <div className="mt-4">
                            <Button
                              style={{
                                background: (isLocked || !ipConsent || !locationConsent)
                                  ? "rgb(236 228 230)"
                                  : "linear-gradient(90deg, #394280, #3d3978)",
                                border: "1px solid #fff",
                                color: (isLocked || !ipConsent || !locationConsent) ? "black" : "#fff",
                              }}
                              className="btn w-100"
                              type="submit"
                              disabled={isLocked || !ipConsent || !locationConsent}
                            >
                              {isLocked ? (
                                <>
                                  <i className="ri-lock-line me-2"></i>
                                  Account Locked
                                </>
                              ) : (
                                <>
                                  <i className="ri-login-box-line me-2"></i>
                                  Sign In
                                </>
                              )}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            color="success"
                            className="btn-load  w-100"
                            outline
                            disabled
                          >
                            <span className="d-flex align-items-center">
                              <Spinner size="sm" className="flex-shrink-0" />
                              <span className="flex-grow-1 ms-2">
                                Logging in...
                              </span>
                            </span>
                          </Button>
                        )}

                        {/* Security Notice */}
                        <div className="mt-4 text-center">
                          <div className="alert alert-info mb-0" role="alert">
                            <i className="ri-information-line me-2"></i>
                            <small>
                              <strong>Security Notice:</strong> Your login
                              attempts are monitored. After 3 failed attempts,
                              your account will be locked for 24 hours.
                            </small>
                          </div>
                        </div>

                        {/* <div className="mt-4 text-center">
                          <div className="signin-other-title">
                            <h5 className="fs-13 mb-4 title">Sign In with</h5>
                          </div>
                          <div>
                          </div>
                        </div> */}
                      </Form>
                    </div>
                  </CardBody>
                </Card>

                {/* <div className="mt-4 text-center">
                  <p className="mb-0">
                    Forgot Password ?{" "}
                    <Link
                      to="/forgot-password"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      {" "}
                      Forgot{" "}
                    </Link>{" "}
                  </p>
                </div> */}
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>

      {/* Security Information Modal */}
      <Modal
        isOpen={securityInfoModal}
        toggle={() => setSecurityInfoModal(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setSecurityInfoModal(false)}>
          <i className="ri-shield-keyhole-line text-primary me-2"></i>
          Why We Need These Permissions
        </ModalHeader>
        <ModalBody>
          <div className="security-info-content">
            <div className="mb-4">
              <h5 className="text-primary mb-3">
                <i className="ri-security-line me-2"></i>
                Your Security is Our Priority
              </h5>
              <p className="text-muted">
                We implement multiple security layers to protect your account
                from unauthorized access and cyber threats.
              </p>
            </div>

            <div className="border-bottom pb-3 mb-3">
              <h6 className="text-success mb-2">
                <i className="ri-shield-check-line me-2"></i>
                IP Address Tracking
              </h6>
              <ul className="text-muted mb-0">
                <li>
                  <strong>Detect suspicious logins</strong> from unusual
                  locations or countries
                </li>
                <li>
                  <strong>Alert you</strong> when login attempts occur from new
                  devices
                </li>
                <li>
                  <strong>Block brute force attacks</strong> by monitoring
                  repeated failed attempts
                </li>
                <li>
                  <strong>Forensic analysis</strong> in case of security
                  incidents
                </li>
              </ul>
            </div>

            <div className="border-bottom pb-3 mb-3">
              <h6 className="text-success mb-2">
                <i className="ri-map-pin-line me-2"></i>
                GPS Location Tracking
              </h6>
              <ul className="text-muted mb-0">
                <li>
                  <strong>Prevent unauthorized access</strong> by verifying
                  login locations
                </li>
                <li>
                  <strong>Track security threats</strong> with precise
                  geographical data
                </li>
                <li>
                  <strong>Enable location-based alerts</strong> for account
                  protection
                </li>
                <li>
                  <strong>Compliance with security policies</strong> for
                  sensitive operations
                </li>
              </ul>
            </div>

            <div className="border-bottom pb-3 mb-3">
              <h6 className="text-danger mb-2">
                <i className="ri-lock-line me-2"></i>
                Account Protection Measures
              </h6>
              <ul className="text-muted mb-0">
                <li>
                  <strong>3 Failed Attempts = 24 Hour Lock:</strong> Protects
                  against password guessing
                </li>
                <li>
                  <strong>Rate Limiting:</strong> Maximum 10 login attempts per
                  15 minutes
                </li>
                <li>
                  <strong>Real-time Monitoring:</strong> All login activities
                  are logged and tracked
                </li>
                <li>
                  <strong>Admin Dashboard:</strong> Superadmins can review all
                  login attempts
                </li>
              </ul>
            </div>

            <div className="bg-light p-3 rounded">
              <h6 className="text-info mb-2">
                <i className="ri-lock-password-line me-2"></i>
                Data Privacy & Security
              </h6>
              <ul className="text-muted mb-0 small">
                <li>
                  All data is <strong>encrypted</strong> using industry-standard
                  protocols
                </li>
                <li>
                  Your information is <strong>never shared</strong> with third
                  parties
                </li>
                <li>
                  Passwords are <strong>hashed with bcrypt</strong>
                </li>
                <li>
                  JWT tokens are used for <strong>secure authentication</strong>
                </li>
                <li>
                  Stored data is <strong>automatically deleted</strong> after 90
                  days
                </li>
              </ul>
            </div>

            <div className="alert alert-warning mt-3 mb-0">
              <i className="ri-information-line me-2"></i>
              <strong>Note:</strong> Both permissions are required to proceed.
              Without these security measures, we cannot guarantee the safety of
              your account.
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setSecurityInfoModal(false)}>
            <i className="ri-check-line me-1"></i>I Understand
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(Login);
